import { ApiError } from "../../services/apiClient";
import type { MediaJoin, OtpDelivery, SessionView } from "../../services/publicSession";
import type { MediaDevicePreferences } from "../../services/mediaDevicePreferences";

// Pure state machine of the public root ("/") and guest link ("/join") experiences. The screen
// is always derived from the latest SERVER view plus two local facts the server cannot know:
// whether this tab has approved its devices (readiness must be redone after a refresh) and
// whether the media join for the started call is in hand. Unit-tested without a DOM.

export type ClosedReason = "cancelled" | "expired" | "invalid" | "signedOut";

export type PublicScreen =
  | { kind: "loading" }
  | { kind: "phone" }
  | { kind: "otp"; challengeId: string; phoneDisplay: string; delivery: OtpDelivery }
  | { kind: "guestPhone" }
  | { kind: "readiness" }
  | { kind: "waiting" }
  | { kind: "starting" }
  | { kind: "incall"; join: MediaJoin }
  | { kind: "ended" }
  | { kind: "closed"; reason: ClosedReason };

export interface PublicFlowState {
  screen: PublicScreen;
  view: SessionView | null;
  prefs: MediaDevicePreferences | null;
  join: MediaJoin | null;
  error: PublicErrorKey | null;
}

export type PublicFlowAction =
  | { type: "signedOut" }                                        // no / invalid session → start
  | { type: "otpRequested"; challengeId: string; phoneDisplay: string; delivery: OtpDelivery }
  | { type: "changePhone" }
  | { type: "view"; view: SessionView }                          // any server state response
  | { type: "devicesReady"; prefs: MediaDevicePreferences }
  | { type: "devicesReset" }                                     // left the waiting room → re-check devices
  | { type: "joined"; join: MediaJoin }
  | { type: "callEnded" }
  | { type: "invalidInvite" }
  | { type: "error"; error: PublicErrorKey | null };

export const initialPublicFlow: PublicFlowState = { screen: { kind: "loading" }, view: null, prefs: null, join: null, error: null };

export function publicFlowReducer(state: PublicFlowState, action: PublicFlowAction): PublicFlowState {
  switch (action.type) {
    case "signedOut":
      return { ...initialPublicFlow, screen: { kind: "phone" } };
    case "otpRequested":
      return { ...state, screen: { kind: "otp", challengeId: action.challengeId, phoneDisplay: action.phoneDisplay, delivery: action.delivery }, error: null };
    case "changePhone":
      return { ...state, screen: { kind: "phone" }, error: null };
    case "view": {
      // Errors are cleared when the user acts again, not by background polls.
      const next = { ...state, view: action.view };
      return { ...next, screen: screenFor(next) };
    }
    case "devicesReady": {
      const next = { ...state, prefs: action.prefs, error: null };
      return { ...next, screen: screenFor(next) };
    }
    case "devicesReset": {
      const next = { ...state, prefs: null, join: null };
      return { ...next, screen: screenFor(next) };
    }
    case "joined": {
      // One call per session: a second callAccepted / media-token response for the same call
      // (hub event + REST backstop) is ignored; a different call id is never adopted.
      if (state.join) return state;
      if (state.view?.callId != null && state.view.callId !== action.join.callId) return state;
      const next = { ...state, join: action.join };
      return { ...next, screen: screenFor(next) };
    }
    case "callEnded":
      return { ...state, join: null, screen: { kind: "ended" } };
    case "invalidInvite":
      return { ...initialPublicFlow, screen: { kind: "closed", reason: "invalid" } };
    case "error":
      return { ...state, error: action.error };
    default:
      return state;
  }
}

/** Derive the visible screen from the server view + local readiness/join facts. */
export function screenFor(s: Pick<PublicFlowState, "view" | "prefs" | "join" | "screen">): PublicScreen {
  const v = s.view;
  if (!v) return s.screen;
  switch (v.step) {
    case "guestPhone":
      return { kind: "guestPhone" };
    case "readiness":
      return s.prefs ? { kind: "waiting" } : { kind: "readiness" };
    case "waiting":
      // After a refresh the devices must be approved again before waiting/joining.
      return s.prefs ? { kind: "waiting" } : { kind: "readiness" };
    case "inCall":
      if (!s.prefs) return { kind: "readiness" };
      return s.join ? { kind: "incall", join: s.join } : { kind: "starting" };
    case "ended":
      return { kind: "ended" };
    case "cancelled":
      return { kind: "closed", reason: "cancelled" };
    case "expired":
      return { kind: "closed", reason: "expired" };
    default:
      return s.screen;
  }
}

/** Screens during which the client keeps a hub connection and polls (presence heartbeat). */
export function needsLiveSync(screen: PublicScreen): boolean {
  return screen.kind === "waiting" || screen.kind === "starting" || screen.kind === "incall";
}

/** Poll cadence: fast while waiting for the other party, slower once the call runs. */
export function pollIntervalMs(screen: PublicScreen): number | null {
  if (screen.kind === "waiting" || screen.kind === "starting") return 3_000;
  if (screen.kind === "incall") return 10_000;
  return null;
}

// ---- routing ------------------------------------------------------------------------

/** Retired entry paths (old officer workstation + POC demo entries) — all land on the clean "/". */
export const LEGACY_ENTRY_PATHS = ["/station", "/demo/traffic", "/demo/ttraffic", "/demo/field"] as const;

export function isLegacyEntryPath(pathname: string): boolean {
  const p = pathname.toLowerCase().replace(/\/+$/, "");
  return (LEGACY_ENTRY_PATHS as readonly string[]).includes(p);
}

/**
 * "/" is only ever the public phone/OTP entry. Any query string or fragment on it — e.g. a
 * legacy `/?token=…` workstation link or a `?callId=` join — is dropped (never forwarded, never
 * used) by navigating to the bare "/". The clean URL then renders the OTP entry, so there is no
 * redirect loop and no way to skip OTP via the root URL.
 */
export function rootNeedsCleanup(search: string, hash: string): boolean {
  return (search !== "" && search !== "?") || (hash !== "" && hash !== "#");
}

/**
 * Boot-time capture of an officer `?token=` into tab storage applies only off the public
 * surface: never on "/", "/join" or a retired entry path, so a legacy token in such a URL is
 * neither stored nor reused.
 */
export function capturesOfficerTokenAt(pathname: string): boolean {
  const p = pathname.toLowerCase().replace(/\/+$/, "") || "/";
  return p !== "/" && p !== "/join" && !isLegacyEntryPath(p);
}

/** Guest link token from the URL fragment (`/join#<token>`); fragments never reach the server or proxy logs. */
export function inviteTokenFromHash(hash: string): string {
  const raw = hash.startsWith("#") ? hash.slice(1) : hash;
  const token = raw.startsWith("t=") ? raw.slice(2) : raw;
  return /^[0-9]+\.[A-Za-z0-9_-]{20,}$/.test(token) ? token : "";
}

/** Absolute invitation URL for the copy action (built locally; never sent to logs). */
export function absoluteInviteUrl(origin: string, invitePath: string): string {
  return origin.replace(/\/+$/, "") + invitePath;
}

// ---- input + errors -----------------------------------------------------------------

/** Keep only digits from typed/pasted OTP input (mobile keyboards, "12 34", etc.). */
export function sanitizeOtpInput(raw: string): string {
  return raw.replace(/\D+/g, "").slice(0, 8);
}

export type PublicErrorKey =
  | "invalidPhone" | "cannotContinue" | "tooMany" | "codeRejected" | "codeRequired"
  | "sameParty" | "unavailable" | "deliveryUnavailable" | "generic";

export function publicErrorKey(err: unknown): PublicErrorKey {
  if (!(err instanceof ApiError)) return "unavailable";
  switch (err.problem.type) {
    case "VC-PUB-001": return "cannotContinue";
    case "VC-PUB-002": return "invalidPhone";
    case "VC-PUB-003": return "tooMany";
    case "VC-PUB-004": return "codeRejected";
    case "VC-PUB-008": return "sameParty";
    case "VC-PUB-012": return "unavailable";
    case "VC-PUB-013": return "deliveryUnavailable";
    default: return err.status === 429 ? "tooMany" : err.status >= 500 ? "unavailable" : "generic";
  }
}

/** A 401 from a public endpoint means the scoped session is gone (revoked, superseded, expired). */
export function isSessionGone(err: unknown): boolean {
  return err instanceof ApiError && err.status === 401;
}

/** Invitation no longer usable (cancelled/expired/ended → 410, or unknown link → 404). */
export function isInvitationGone(err: unknown): boolean {
  return err instanceof ApiError && (err.status === 410 || err.status === 404);
}
